/**
 * SearchableMultiSelect Component
 *
 * A searchable multi-select dropdown using Fluent UI Combobox.
 * Used for selecting multiple items from a large list with filtering.
 *
 * Features:
 * - Search/filter functionality with text input
 * - Multi-selection with checkmarks
 * - Selected items displayed as removable tags
 * - Optional availability badges
 * - Disabled state for unavailable items
 */

import React, { useState, useMemo } from 'react';
import {
  Combobox,
  Option,
  Badge,
  Tag,
  Label,
  makeStyles,
  tokens,
  useId,
} from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';

export interface SearchableMultiSelectItem {
  id: string;
  label: string;
  availability?: number;
}

export interface SearchableMultiSelectProps {
  items: SearchableMultiSelectItem[];
  selectedIds: string[];
  onSelectionChange: (selectedIds: string[]) => void;
  placeholder?: string;
  label?: string;
  showAvailability?: boolean;
  disableUnavailable?: boolean;
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  selectedTags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalXS,
  },
  tag: {
    maxWidth: '200px',
  },
  optionContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  availabilityBadge: {
    marginLeft: tokens.spacingHorizontalS,
  },
  noResults: {
    padding: tokens.spacingVerticalS,
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
});

export const SearchableMultiSelect: React.FC<SearchableMultiSelectProps> = ({
  items,
  selectedIds,
  onSelectionChange,
  placeholder = 'Search and select...',
  label,
  showAvailability = false,
  disableUnavailable = false,
}) => {
  const styles = useStyles();
  const comboboxId = useId('searchable-multiselect');
  const [searchQuery, setSearchQuery] = useState('');

  // Filter items based on search query
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return items;
    const query = searchQuery.toLowerCase();
    return items.filter(item =>
      item.label.toLowerCase().includes(query)
    );
  }, [items, searchQuery]);

  // Get selected items for tag display
  const selectedItems = useMemo(() => {
    return selectedIds
      .map(id => items.find(item => item.id === id))
      .filter((item): item is SearchableMultiSelectItem => item !== undefined);
  }, [selectedIds, items]);

  // Handle selection change
  const handleOptionSelect = (
    _event: React.SyntheticEvent,
    data: { selectedOptions: string[] }
  ) => {
    onSelectionChange(data.selectedOptions);
  };

  // Handle tag removal
  const handleTagDismiss = (idToRemove: string) => {
    onSelectionChange(selectedIds.filter(id => id !== idToRemove));
  };

  // Handle search input change
  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSearchQuery(event.target.value);
  };

  // Determine if an item should be disabled
  const isItemDisabled = (item: SearchableMultiSelectItem): boolean => {
    if (!disableUnavailable) return false;
    // If already selected, don't disable (allow deselection)
    if (selectedIds.includes(item.id)) return false;
    // Disable if availability is 0
    return item.availability === 0;
  };

  return (
    <div className={styles.container}>
      {label && <Label htmlFor={comboboxId}>{label}</Label>}

      {/* Selected items as tags */}
      {selectedItems.length > 0 && (
        <div className={styles.selectedTags} data-testid="selected-tags">
          {selectedItems.map(item => (
            <Tag
              key={item.id}
              className={styles.tag}
              dismissible
              dismissIcon={<DismissRegular />}
              onClick={() => handleTagDismiss(item.id)}
              size="small"
              appearance="brand"
            >
              {item.label}
            </Tag>
          ))}
        </div>
      )}

      {/* Searchable combobox */}
      <Combobox
        id={comboboxId}
        aria-label={label || 'Select items'}
        multiselect
        placeholder={placeholder}
        value={searchQuery}
        selectedOptions={selectedIds}
        onOptionSelect={handleOptionSelect}
        onChange={handleInputChange}
        freeform
      >
        {filteredItems.length === 0 ? (
          <Option key="no-results" value="" disabled text="No matching items">
            <span className={styles.noResults}>No matching items found</span>
          </Option>
        ) : (
          filteredItems.map(item => (
            <Option
              key={item.id}
              value={item.id}
              text={item.label}
              disabled={isItemDisabled(item)}
            >
              <div className={styles.optionContent}>
                <span>{item.label}</span>
                {showAvailability && item.availability !== undefined && (
                  <Badge
                    appearance="tint"
                    color={item.availability > 0 ? 'informative' : 'subtle'}
                    className={styles.availabilityBadge}
                  >
                    {item.availability}
                  </Badge>
                )}
              </div>
            </Option>
          ))
        )}
      </Combobox>
    </div>
  );
};
